import React, { useRef, useState } from "react";
import { gql } from "@apollo/client";
import classnames from "classnames";
import type { Swiper as SwiperType } from "swiper";
import { Packages, ProductEnum } from "../../../../__generated__/graphql";
import { ContentfulComponentType } from "../types";
import { ContentfulC2A } from "../ContentfulC2A";
import TextFieldsV2 from "../TextFieldsV2";
import { getPackageConfig } from "../ContentfulPackages/getPackageConfig";
import { PackageConfig, Package } from "../ContentfulPackages/types";
import { PackageCard, Modal, CarouselControls } from "@/ui-kit";
import MobileFullPackages from "../ContentfulPackages/MobileFullPackages/MobileFullPackages";
import InsurancePackagesTable from "../ContentfulPackages/InsurancePackagesTable/InsurancePackagesTable";
import ShowBlockOnMedia from "../../../ui-kit/ShowBlockOnMedia";
import { useWebTranslation, Trans } from "../../../translations";
import { formatCurrency } from "../../../utils/currency";
import { getOrg } from "../../../utils/site";
import { Swiper, SwiperSlide } from "swiper/react";
import { Mousewheel } from "swiper/modules";
import Gutter from "ui-kit/Gutter";
import styles from "./ContentfulPackagesV2.module.css";
import { ChevronRight } from "ui-kit/icons";
import "swiper/css";
import "swiper/css/mousewheel";

const convertProductsToConfigs = (products: (string | null)[] | null) =>
  (products ?? [])
    .map((product) =>
      getPackageConfig({
        product: product?.toLowerCase() as ProductEnum,
      })
    )
    .filter((config) => config !== null) as PackageConfig[];

const PackageCardFromConfig = ({
  pack,
  config,
  onCompare,
  className,
  reserveCoverageSubtext,
}: {
  pack: Package;
  config: PackageConfig;
  onCompare: () => void;
  className?: string;
  reserveCoverageSubtext?: boolean;
}) => {
  const { t, i18n } = useWebTranslation();
  const org = getOrg();

  // Coverage value and USP bullets come from Lokalise, keyed per tier, so the
  // content team can add, edit and reorder them without a deploy:
  //   package_cards.coverage_value.{org}.{tier}  -> string. May use {{sum}} (the
  //     formatted limit, passed in below) and a <small> tag for the secondary line,
  //     e.g. "{{sum}}/Jahr" or "Unbegrenzt<br><small>ab dem zweiten Jahr</small>".
  //   package_cards.usps.{org}.{tier}            -> object/array of bullet strings
  // Read USPs off the raw i18n instance (not the org-scoped `t`, which appends a
  // context suffix) so the keys resolve verbatim. No keys yet => no bullets, and
  // coverage falls back to the numeric limit + "/year".
  const formattedSum = formatCurrency(pack.limit / 100, {
    maximumFractionDigits: 0,
  });
  const coverageKey = `package_cards.coverage_value.${org}.${pack.name}`;
  const coverageValue = i18n?.exists(coverageKey, { ns: "web" }) ? (
    <Trans
      i18nKey={coverageKey}
      values={{ sum: formattedSum }}
      components={{ small: <small /> }}
    />
  ) : (
    `${formattedSum}/${t("global.date.year")}`
  );

  // i18next returnObjects gives an array, or an object keyed by index, or (when
  // the key is missing) a string — normalise to a list of bullet strings.
  const uspData = i18n?.t(`package_cards.usps.${org}.${pack.name}`, {
    ns: "web",
    returnObjects: true,
  }) as unknown;
  const bulletStrings = Array.isArray(uspData)
    ? uspData
    : uspData && typeof uspData === "object"
    ? Object.values(uspData)
    : [];
  const usps = bulletStrings.map((text) => ({
    text: String(text),
    icon: "checkmark-circle" as const,
  }));

  // Both liability packages share name "liability", so their per-card content is
  // derived here rather than from per-tier Lokalise keys: coverage is a fixed sum
  // (no "/year"), the single bullet is the deductible (Selbstbeteiligung), and the
  // 0 € deductible plan is the popular one.
  const isLiability = config.product === ProductEnum.liability;
  const liabilityUsps = [
    {
      text: `${formatCurrency((pack.deductible ?? 0) / 100, {
        maximumFractionDigits: 0,
      })} ${t("ger.insurance_packages.legend.liability_deductible.title")}`,
      icon: "checkmark-circle" as const,
    },
  ];
  const popular = isLiability ? pack.deductible === 0 : pack.popular;

  return (
    <PackageCard
      className={className}
      name={t(pack.title)}
      description={t(pack.subTitle)}
      iconSrc={pack.newImage}
      popular={popular}
      popularLabel={popular ? t("global.most_popular") : undefined}
      coverageLabel={t("global.coverage")}
      coverageValue={isLiability ? formattedSum : coverageValue}
      reserveCoverageSubtext={reserveCoverageSubtext}
      usps={isLiability ? liabilityUsps : usps}
      gradient={pack.redesignGradient}
      readMoreLabel={t("global.read_more")}
      onReadMore={onCompare}
    />
  );
};

export const ContentfulPackagesV2: ContentfulComponentType<Packages> = ({
  products,
  highlight,
  title,
  text,
  c2A,
}) => {
  const { t, i18n } = useWebTranslation();
  const org = getOrg();
  const configs = convertProductsToConfigs(products);
  const config = configs[0];
  const [compareOpen, setCompareOpen] = useState(false);
  const [activeSlide, setActiveSlide] = useState(1);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const swiperRef = useRef<SwiperType | null>(null);

  const syncEdges = (swiper: SwiperType) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  if (!config) return null;

  // 4+ cards (e.g. Germany health) don't fit a desktop row, so always use the
  // carousel. 3 or fewer render as a centered row on desktop.
  const useCarousel = config.packages.length >= 4;

  // Liability has two packages that share the name "liability"; order them
  // base Liability (higher deductible) before Liability Plus (0 € deductible).
  const displayPackages =
    config.product === ProductEnum.liability
      ? [...config.packages].sort(
          (a, b) => (b.deductible ?? 0) - (a.deductible ?? 0)
        )
      : config.packages;

  // Only reserve a second coverage line when at least one card in this set
  // actually has the subtext (authored in Lokalise with a <br>/<small>) — if
  // none do, every card is single-line and already aligned. Liability cards
  // use a plain sum with no subtext.
  const anyCoverageSubtext =
    config.product !== ProductEnum.liability &&
    displayPackages.some((pack) => {
      const key = `package_cards.coverage_value.${org}.${pack.name}`;
      if (!i18n?.exists(key, { ns: "web" })) return false;
      return /<br|<small/i.test(String(i18n.t(key, { ns: "web" })));
    });

  return (
    <div className="container">
      <section className={styles.section}>
        <div className={styles.socialProof}>
          <p className={styles.socialProofText}>
            {t("global.number_of_paws", {
              defaultValue: "Hundreds of thousands insured paws",
            })}
          </p>
        </div>
        <TextFieldsV2 title={title} text={text} center />

        {!useCarousel && (
          <div className={styles.desktopGrid}>
            <div className={styles.grid}>
              {displayPackages.map((pack) => (
                <PackageCardFromConfig
                  key={`${pack.name}-${pack.deductible ?? ""}`}
                  pack={pack}
                  config={config}
                  onCompare={() => setCompareOpen(true)}
                  className={styles.gridCard}
                  reserveCoverageSubtext={anyCoverageSubtext}
                />
              ))}
            </div>
          </div>
        )}

        <div
          className={classnames(styles.swiperContainer, {
            [styles.swiperContainerAlways]: useCarousel,
          })}
        >
          <Swiper
            slidesPerView={1.25}
            spaceBetween={16}
            centeredSlides
            initialSlide={1}
            modules={[Mousewheel]}
            mousewheel={{ forceToAxis: true }}
            breakpoints={{
              400: { slidesPerView: "auto" },
            }}
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
              syncEdges(swiper);
            }}
            onSlideChange={(swiper) => {
              setActiveSlide(swiper.activeIndex);
              syncEdges(swiper);
            }}
          >
            {displayPackages.map((pack) => (
              <SwiperSlide
                key={`${pack.name}-${pack.deductible ?? ""}`}
                className={styles.slide}
              >
                <PackageCardFromConfig
                  pack={pack}
                  config={config}
                  onCompare={() => setCompareOpen(true)}
                  reserveCoverageSubtext={anyCoverageSubtext}
                />
              </SwiperSlide>
            ))}
          </Swiper>
          <CarouselControls
            count={config.packages.length}
            activeIndex={activeSlide}
            onDotClick={(index) => swiperRef.current?.slideTo(index)}
            onPrev={() => swiperRef.current?.slidePrev()}
            onNext={() => swiperRef.current?.slideNext()}
            prevDisabled={isBeginning}
            nextDisabled={isEnd}
            className={classnames(styles.controls, {
              [styles.controlsDesktopHidden]: config.packages.length <= 4,
            })}
          />
        </div>

        {c2A && (
          <div className={styles.cta}>
            <ContentfulC2A {...c2A} />
          </div>
        )}

        <div className={styles.compareCta}>
          <button
            type="button"
            className={styles.compareLink}
            onClick={() => setCompareOpen(true)}
          >
            {t("global.compare_plans")}
            <ChevronRight size={16} />
          </button>
        </div>

        <Modal
          isOpen={compareOpen}
          onClose={() => setCompareOpen(false)}
          title=""
        >
          <ShowBlockOnMedia desktop={false} tablet={true} mobile={true}>
            <MobileFullPackages
              config={config}
              defaultSelectedIndex={1}
              expanded
              isV2
            />
          </ShowBlockOnMedia>
          <ShowBlockOnMedia desktop={true} tablet={false} mobile={false}>
            <InsurancePackagesTable config={config} isV2 />
          </ShowBlockOnMedia>
          <div className={styles.compareBottomSpacer} />
        </Modal>
      </section>
    </div>
  );
};

ContentfulPackagesV2.fragment = gql`
  fragment Packages on ComponentPackages {
    products
    layout
    highlight
    title
    text
    theme
    c2A {
      ...C2A
    }
    sys {
      id
      publishedAt
    }
  }
`;
